import React from 'react';
import styled from 'styled-components';

const ChangelogContainer = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: white;
`;

const ContentWrapper = styled.div`
    max-width: 900px;
    margin: 0 auto;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 50px;
`;

const Title = styled.h1`
    font-size: 3rem;
    margin-bottom: 15px;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const Description = styled.p`
    font-size: 1rem;
    opacity: 0.8;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
`;

const VersionSection = styled.section`
    margin-bottom: 50px;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }

    @media (max-width: 768px) {
        padding: 20px;
    }
`;

const VersionTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 10px;
    color: #4a9eff;
    display: flex;
    align-items: center;
    gap: 10px;

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

const VersionDate = styled.p`
    font-size: 0.9rem;
    opacity: 0.7;
    margin-bottom: 25px;
    font-style: italic;
`;

const ChangeCategory = styled.div`
    margin-bottom: 25px;
`;

const CategoryTitle = styled.h3`
    font-size: 1.3rem;
    margin-bottom: 15px;
    color: #6cceff;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
        content: '';
        width: 4px;
        height: 20px;
        background: #6cceff;
        border-radius: 2px;
    }
`;

const ChangeList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const ChangeItem = styled.li`
    padding: 10px 0;
    padding-left: 25px;
    position: relative;
    line-height: 1.6;
    opacity: 0.9;

    &::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #6cceff;
        font-size: 1.5rem;
        line-height: 1;
    }
`;

const UnreleasedSection = styled(VersionSection)`
    border: 2px dashed rgba(255, 255, 255, 0.3);
    opacity: 0.8;
`;

const ChangelogPage = () => {
    const changelogData = [
        {
            version: '1.0.0',
            date: '2024-11-28',
            changes: {
                Added: [
                    '首頁 (Home page) - 個人介紹頁面，包含技能標籤和導航連結',
                    '履歷頁面 (Resume page) - 完整的履歷展示，支援 PDF 下載',
                    'Deer Memo 頁面 (Music page) - 整合 Spotify 和 SoundCloud 播放器',
                    'Side Projects 頁面 - 展示業餘專案，包含投票功能（咖啡/無酒精調酒）',
                    'Firebase 整合 - 用於 Side Projects 投票數據存儲',
                    'LINE 貼圖連結 - 醜馬2 貼圖商店連結',
                    '眼睛圖標素材 (eye-icon.svg)',
                    'Changelog 頁面 - 網站更新記錄'
                ],
                Features: [
                    '響應式設計，支援手機和桌面設備',
                    '現代化的 UI 設計，使用漸層背景和動畫效果',
                    'React Router 路由系統',
                    'styled-components 樣式管理',
                    '目前任職資訊：8AM (2024/04 ~ Present)'
                ],
                Technical: [
                    'React 19.0.0',
                    'React Router DOM 7.1.5',
                    'styled-components 6.1.15',
                    'Firebase (Firestore)',
                    'html2pdf.js for PDF generation'
                ]
            }
        }
    ];

    const categoryColors = {
        Added: '#27ae60',
        Changed: '#f39c12',
        Deprecated: '#e67e22',
        Removed: '#e74c3c',
        Fixed: '#3498db',
        Security: '#9b59b6',
        Features: '#1abc9c',
        Technical: '#95a5a6'
    };

    return (
        <ChangelogContainer>
            <ContentWrapper>
                <Header>
                    <Title>Changelog</Title>
                    <Subtitle>更新記錄</Subtitle>
                    <Description>
                        記錄網站的所有重要更新和變更
                        <br />
                        Track all notable changes and updates to the website
                    </Description>
                </Header>

                {changelogData.map((version, index) => (
                    <VersionSection key={index}>
                        <VersionTitle>
                            <span>v{version.version}</span>
                        </VersionTitle>
                        <VersionDate>{version.date}</VersionDate>
                        
                        {Object.entries(version.changes).map(([category, items]) => (
                            <ChangeCategory key={category}>
                                <CategoryTitle style={{ color: categoryColors[category] || '#6cceff' }}>
                                    {category}
                                </CategoryTitle>
                                <ChangeList>
                                    {items.map((item, itemIndex) => (
                                        <ChangeItem key={itemIndex}>{item}</ChangeItem>
                                    ))}
                                </ChangeList>
                            </ChangeCategory>
                        ))}
                    </VersionSection>
                ))}

                <UnreleasedSection>
                    <VersionTitle>
                        <span>Unreleased</span>
                    </VersionTitle>
                    <VersionDate>即將推出...</VersionDate>
                    <ChangeCategory>
                        <CategoryTitle style={{ color: '#95a5a6' }}>
                            計劃中的功能
                        </CategoryTitle>
                        <ChangeList>
                            <ChangeItem>更多專案展示</ChangeItem>
                            <ChangeItem>持續優化用戶體驗</ChangeItem>
                        </ChangeList>
                    </ChangeCategory>
                </UnreleasedSection>
            </ContentWrapper>
        </ChangelogContainer>
    );
};

export default ChangelogPage;

